import * as axios from "axios";
import { ArticleExtraData } from "../../../../../../../articlePage/stateInterfaces";
import { DateDisplay } from "../../../../../../commonComponents/inline/dateDisplay/dateDisplay";
import { SiteItemData } from "../../../../../../interfaces";
import { disablePageRefresh, enablePageRefresh } from "../../../../../../siteWidgets/siteWidgetUtils";
import { FlashesMediaItem } from "../../../../../verticalVideoOverlayPlayer/designs/flashesMediaItem/flashesMediaItem";
import { VerticalVideoOverlayPlayer } from "../../../../../verticalVideoOverlayPlayer/verticalVideoOverlayPlayer";
import { getTranslation } from "../../../../autoTickerRowWidget/autoTickerRowTranslations";
import { categoryObj } from "../../../../newYnetTvVerticalDynamic/components/NewYnetTvVerticalDynamicMutamComponenta";
import { addAxiosDateTransformerSettings } from "../../../../../../../utils/network/axiosDateTransformer";
import { ItemJsonVer2 } from "../../../../../../jsonSerializer/widgetJsonInterfacesVer2";
import { buildOngoingRoofPresentation, filterOngoingRoofPopupArticles, mapItemJsonToArticleExtraData } from "../utils/newsFlashUtils";
import { NewsFlashSpecialSlotItem } from "./newsFlashSpecialSlotItem";
import { OngoingRoofEventCluster } from "./ongoingRoofEventCluster";
import { OngoingRoofEventPopup } from "./ongoingRoofEventPopup";
require("./m_ynet_newsflashSpecialMobileSlotList.less");

interface NewsFlashSpecialMobileSlotListProps {
    isLTR: boolean
    domain: string
    category: categoryObj
    languageCode: string
    tickerArticles: ArticleExtraData[]
    overlayItems?: SiteItemData[]
    /** Page-184 mapped articles for ongoing-event popup snapshot */
    overlayArticles?: ArticleExtraData[]
    verticalBackgroundImage?: string
    isFirstInterval: boolean
    roofOngoingEvent: boolean
    /** Opt-in: only newsFlashSpecial mobile parent should set this (keeps flash-page reuse unchanged) */
    enableOngoingRoofUi?: boolean
    excludedOngoingRoofs?: string[]
    shouldDisplayLoader: boolean
    shouldDisplayTicker: boolean
    highlightingDuration: number
    tickerBottomLink?: string
    tickerBottomTitle?: string
    accordionOpenState?: boolean
    openedItemIndex?: string
    openedItems?: string[]
    onToggleItem?: (id: string) => void
    onOverlayOpen?: () => void
    onOverlayClose?: () => void
    /** Refresh page-184 data when opening the ongoing popup */
    onOngoingPopupOpen?: () => void
    commercialItemsNum?: number
    commercialRepetitionsNum?: number
    commercialBannerId?: string
}

interface NewsFlashSpecialMobileSlotListState {
    selectedVideo: number
    isOverlayOpen: boolean
    ongoingPopupRoof: string | null
    ongoingPopupArticles: ArticleExtraData[]
}

export class NewsFlashSpecialMobileSlotList extends React.Component<NewsFlashSpecialMobileSlotListProps, NewsFlashSpecialMobileSlotListState> {

    constructor(props) {
        super(props);
        this.state = {
            selectedVideo: -1,
            isOverlayOpen: false,
            ongoingPopupRoof: null,
            ongoingPopupArticles: [],
        }
    }

    private openMediaDisplayView = (videoIndex: number) => {
        const { onOverlayOpen } = this.props;

        this.setState({ isOverlayOpen: true, selectedVideo: videoIndex });
        disablePageRefresh();
        onOverlayOpen && onOverlayOpen();
    }

    private closeMediaDisplayView = () => {
        const { onOverlayClose } = this.props;

        this.setState({ isOverlayOpen: false, selectedVideo: -1 });
        enablePageRefresh();
        onOverlayClose && onOverlayClose();
    }

    private openOngoingPopup = (roof: string) => {
        const { overlayArticles, onOngoingPopupOpen } = this.props;
        onOngoingPopupOpen && onOngoingPopupOpen();

        // Show immediately from current page-184 cache, then refresh snapshot from the same endpoint
        const cached = filterOngoingRoofPopupArticles(overlayArticles || [], roof);
        this.setState({
            ongoingPopupRoof: roof,
            ongoingPopupArticles: cached,
        });

        const axiosInstance = axios.create(addAxiosDateTransformerSettings());
        axiosInstance.get("/iphone/json/mobile/v2/page/184")
            .then((res: any) => {
                if (this.state.ongoingPopupRoof !== roof) {
                    return;
                }
                const items: ItemJsonVer2[] = res.data.components.find((component) => component.name === "flashesHeadlines").items;
                const mapped = (items || []).map(item => mapItemJsonToArticleExtraData(item));
                this.setState({
                    ongoingPopupArticles: filterOngoingRoofPopupArticles(mapped, roof),
                });
            })
            .catch(err => console.error("error getting ongoing event popup data:", err));
    }

    private closeOngoingPopup = () => {
        const { onOverlayClose } = this.props;
        this.setState({
            ongoingPopupRoof: null,
            ongoingPopupArticles: [],
        });
        onOverlayClose && onOverlayClose();
    }

    private getAccordionState = (article: ArticleExtraData) => {
        const { onToggleItem, accordionOpenState, openedItemIndex, openedItems } = this.props;
        return onToggleItem
            ? (accordionOpenState || article.articleId === openedItemIndex || (openedItems && openedItems.includes(article.articleId)))
            : accordionOpenState;
    }

    private renderSlotItems = () => {
        const { tickerArticles, isFirstInterval, languageCode, tickerBottomLink, tickerBottomTitle,
            highlightingDuration, isLTR, roofOngoingEvent, enableOngoingRoofUi, excludedOngoingRoofs, onToggleItem,
            commercialItemsNum, commercialRepetitionsNum, commercialBannerId } = this.props;

        const visibleNodes: any[] = [];
        const itemList = tickerArticles;
        const itemLength = itemList.length;
        const bannerIdPrefix = commercialBannerId || "ads.banner.inline";
        const presentation = enableOngoingRoofUi ? buildOngoingRoofPresentation(itemList, excludedOngoingRoofs) : null;

        let i = 0;
        while (i < itemLength) {
            const article = itemList[i];
            const meta = presentation && presentation[i];

            if (meta && meta.isClusterStart && meta.clusterSize > 1) {
                const clusterArticles = itemList.slice(i, i + meta.clusterSize);
                const clusterIndexes: number[] = [];
                const clusterMetas = [];
                for (let c = 0; c < meta.clusterSize; c++) {
                    clusterIndexes.push(i + c);
                    clusterMetas.push(presentation[i + c]);
                }

                visibleNodes.push(
                    <OngoingRoofEventCluster
                        key={`ongoing-cluster-${meta.clusterRoof}-${article.articleId}`}
                        roof={meta.clusterRoof}
                        articles={clusterArticles}
                        articleIndexes={clusterIndexes}
                        metas={clusterMetas}
                        isLTR={isLTR}
                        languageCode={languageCode}
                        isFirstInterval={isFirstInterval}
                        highlightingDuration={highlightingDuration}
                        tickerBottomLink={tickerBottomLink}
                        tickerBottomTitle={tickerBottomTitle}
                        accordionOpenState={this.props.accordionOpenState}
                        openedItemIndex={this.props.openedItemIndex}
                        openedItems={this.props.openedItems}
                        onToggleItem={onToggleItem}
                        openMediaDisplayView={this.openMediaDisplayView}
                        onLiveUpdatesClick={this.openOngoingPopup}
                    />
                );
                i += meta.clusterSize;
                continue;
            }

            const previousRoof = i > 0 ? itemList[i - 1].roof : null;
            const nextRoof = i < itemList.length - 1 ? itemList[i + 1].roof : null;

            visibleNodes.push(
                <NewsFlashSpecialSlotItem
                    index={i}
                    isLTR={isLTR}
                    article={article}
                    nextRoof={nextRoof}
                    key={article.articleId}
                    languageCode={languageCode}
                    previousRoof={previousRoof}
                    onRowItemToggle={onToggleItem}
                    ongoingMeta={meta || undefined}
                    isFirstInterval={isFirstInterval}
                    roofOngoingEvent={roofOngoingEvent}
                    tickerBottomLink={tickerBottomLink}
                    tickerBottomTitle={tickerBottomTitle}
                    highlightingDuration={highlightingDuration}
                    openMediaDisplayView={this.openMediaDisplayView}
                    accordionOpenState={this.getAccordionState(article)}
                />
            );
            i++;
        }

        if (itemLength > 0 && commercialItemsNum && commercialItemsNum > 0 && commercialRepetitionsNum && commercialRepetitionsNum > 0) {
            const availableRepetitions = Math.floor(itemLength / commercialItemsNum);
            const numberOfTaboolaItems = availableRepetitions < commercialRepetitionsNum ? availableRepetitions : commercialRepetitionsNum;

            for (let adIndex = 0; adIndex < numberOfTaboolaItems; adIndex++) {
                visibleNodes.splice(commercialItemsNum * (adIndex + 1) + adIndex, 0, <div id={`${bannerIdPrefix}.${adIndex + 1}`} key={`ad-${adIndex}`} />);
            }
        }

        return visibleNodes;
    }

    public render() {
        const { isOverlayOpen, selectedVideo, ongoingPopupRoof, ongoingPopupArticles } = this.state;
        const { shouldDisplayLoader, overlayItems, highlightingDuration, isLTR, domain, category, languageCode, verticalBackgroundImage } = this.props;
        const lang = languageCode ? languageCode.slice(0, 2) : "he";
        const overlayTitle = getTranslation(lang, "News Flash");
        const items = overlayItems ? overlayItems.map((item) => {
            const minutesSincePublished = Math.floor((new Date().getTime() - new Date(item.dateUpdatedOnSite).getTime()) / (1000 * 60));
            const isRecentItem = minutesSincePublished < (highlightingDuration || 1);
            const date = isRecentItem ? getTranslation(lang, "Now") : <DateDisplay date={item.dateUpdatedOnSite} isMinutesAgo={true} isArticle={true} />;
            return { ...item, date };
        }) : [];

        return (
            <div className="news-flash-special-mobile-slot-list-wrapper">
                {shouldDisplayLoader &&
                    <picture>
                        <source
                            srcSet="https://ynet-pic1.yit.co.il/Common/images/3-dots-loader-dark-mode.webp"
                            media="(prefers-color-scheme: dark)"
                        />
                        <img
                            className="newTickerLoader"
                            src="https://ynet-pic1.yit.co.il/Common/images/3-dots-loader-light-mode.webp"
                            alt=""
                        />
                    </picture>
                }
                
                <div className="news-flash-special-mobile-slot-list">
                    {this.renderSlotItems()}
                </div>

                <VerticalVideoOverlayPlayer
                    isLTR={isLTR}
                    domain={domain}
                    isMobile={true}
                    videoItems={items}
                    category={category}
                    index={selectedVideo}
                    isTitleVisible={true}
                    showPrerollAds={false}
                    isSubTitleVisible={true}
                    overlayTitle={overlayTitle}
                    languageCode={languageCode}
                    isOverlayOpen={isOverlayOpen}
                    selectedVideoIndex={selectedVideo}
                    onVideoOverlayClose={this.closeMediaDisplayView}
                    ItemComponent={(props) => <FlashesMediaItem {...props} />}
                />

                {ongoingPopupRoof && (
                    <OngoingRoofEventPopup
                        isLTR={isLTR}
                        domain={domain}
                        category={category}
                        roof={ongoingPopupRoof}
                        isOpen={!!ongoingPopupRoof}
                        languageCode={languageCode}
                        articles={ongoingPopupArticles}
                        onClose={this.closeOngoingPopup}
                        highlightingDuration={highlightingDuration}
                        verticalBackgroundImage={verticalBackgroundImage}
                    />
                )}
            </div>
        )
    }
}
